// 'use-strict';

import { ICompileService, IScope } from 'angular';
import moment, { Moment } from 'moment';
import { ModalOverlayService } from '../modal-overlay/modal-overlay.service';

const dateFormat = 'DD.MM.YYYY';

const datepickerHtml = /* html */ `
<div class="ab-datepicker-calendar">
  <div class="row date-title">
    <div class="col-2 year-selector"><span ng-click="$ctrl.paginate(-1, 'year')">{{ $ctrl.labels.year - 1}}</span></div>
    <div class="col-8">
      <span class="current-month">{{ $ctrl.labels.month }}</span>
      <span class="current-year">{{ $ctrl.labels.year }}</span>
    </div>
    <div class="col-2 year-selector"><span ng-click="$ctrl.paginate(1, 'year')">{{ $ctrl.labels.year + 1}}</span></div>
  </div>

  <div class="row">
    <div class="col-2 date-selector btn-wrap">
      <button type="button" class="btn-icon" ng-click="$ctrl.paginate(-1, 'month')">
        <svg class="ab-icon"><use xlink:href="#arrow-left"></use></svg>
      </button>
    </div>
    <div class="col-8">
      <div class="row days-header">
        <span>Mo</span>
        <span>Di</span>
        <span>Mi</span>
        <span>Do</span>
        <span>Fr</span>
        <span>Sa</span>
        <span>So</span>
      </div>

      <div class="row days-wrap">
        <span ng-repeat="filler in $ctrl.fillerDays track by $index" class="filler">{{filler}}</span>
        <span ng-repeat="day in $ctrl.daysOfMonth track by $index" ng-click="$ctrl.selectDay(day)"
          ng-class="{'today': $ctrl.labels.day == day}">{{day}}</span>
        <span ng-repeat="disabled in $ctrl.disabledDays track by $index" class="disabled">{{disabled}}</span>
      </div>
    </div>
    <div class="col-2 date-selector btn-wrap">
      <button type="button" class="btn-icon" ng-click="$ctrl.paginate(1, 'month')">
        <svg class="ab-icon"><use xlink:href="#arrow-right"></use></svg>
      </button>
    </div>
  </div>

  <div class="btn-wrap center">
    <button type="button" class="btn-lvl3" ng-click="$ctrl.setToday()">Heute</button>
  </div>
  </div>
`;

/* @ngInject */
export class DatepickerController {
  private selectedDate!: string | null | Date;

  /** Last known raw `selectedDate` binding value to detect external changes in `$doCheck`. */
  private lastBindingValue?: string | null | Date;

  private selectedDateChange!: (event: Date | null | { $event: string | null }) => void;

  private returnType?: 'Date' | 'String';

  private maxDays?: string;

  private currentDate!: moment.Moment;

  private labels: {
    month: string,
    day: number,
    year: number,
    date: string,
  } = {
    month: '',
    day: 0,
    year: 0,
    date: '',
  };

  private fillerDays: null | string[] = null; // filling empty slots before start of month

  private disabledDays: null | number[] = null; // filling days, that are all disabled

  private daysOfMonth: null | number[] = []; // for display in directive

  private modalOverlayService: ModalOverlayService;

  private $compile: ICompileService;

  private $scope: IScope;

  constructor(
    $scope: IScope,
    $compile: ICompileService,
    modalOverlayService: ModalOverlayService,
  ) {
    'ngInject';

    this.modalOverlayService = modalOverlayService;
    this.$compile = $compile;
    this.$scope = $scope;
  }

  $onInit(): void {
    moment.locale('de');
    this.init();
  }

  /** Sync the displayed date, if `selectedDate` is changed from
   * the outside eg. by the warranty quick-set buttons. Only updates
   * the label and calendar — no `selectedDateChange` emit, to avoid
   * a feedback loop through the two-way binding.
   *
   * Uses `$doCheck` instead of `$onChanges`, since the latter is not
   * triggered for AngularJS two-way (`=`) bindings. */
  $doCheck(): void {
    if (this.selectedDate === this.lastBindingValue) return;
    this.lastBindingValue = this.selectedDate;

    if (!this.selectedDate) {
      this.labels.date = '';
      return;
    }

    const newDate = this.selectedDate instanceof Date
      ? moment(this.selectedDate)
      : moment(this.selectedDate, [moment.ISO_8601, dateFormat]);
    if (!newDate.isValid()) return;
    if (this.labels.date === newDate.format(dateFormat)) return;

    this.currentDate = newDate;
    this.labels.date = newDate.format(dateFormat);
    this.populateCalendar();
  }

  private init = () => {
    if (!this.selectedDate) {
      this.currentDate = moment();
    } else {
      this.currentDate = moment(this.selectedDate);
      this.returnDate();
    }

    this.populateCalendar();
  };

  private returnDate(exit?: boolean) {
    if (exit) {
      this.selectedDateChange(null);
      this.labels.date = '';
      return;
    }

    if (!this.returnType || this.returnType === 'String') {
      this.selectedDate = this.currentDate.format(dateFormat);
      this.selectedDateChange({ $event: this.selectedDate });
    } else if (this.returnType === 'Date') {
      this.selectedDate = this.currentDate.toDate();
      this.selectedDateChange(this.selectedDate);
    }

    this.labels.date = moment(this.currentDate).format(dateFormat);
  }

  private showDatepicker = ($event: Event): void => {
    ($event.target as HTMLElement).blur();
    const calendarHtml = datepickerHtml;

    const inputPosition = ($event.target as HTMLElement).getBoundingClientRect();
    const compiledHtml = this.$compile(calendarHtml)(this.$scope);

    this.modalOverlayService.show(compiledHtml, inputPosition);
  };

  private hideDatepicker = (): void => {
    this.modalOverlayService.hide();
  };

  /** Update directive texts for year and month. */
  private updateTexts = () => {
    this.labels.month = this.currentDate.format('MMMM');
    this.labels.year = this.currentDate.year();
  };

  private paginate = (interval: number, time: 'month' | 'year'): void => {
    this.currentDate.add(interval, time);
    this.populateCalendar();
  };

  private selectDay = (day: number) => {
    this.currentDate.set('date', day).toDate();
    this.returnDate();
    this.hideDatepicker();
  };

  public removeDate = () => {
    this.selectedDate = null;
    this.returnDate(true);
    this.hideDatepicker();
  };

  private setToday = () => {
    this.currentDate = moment();
    this.returnDate();
    this.hideDatepicker();
  };

  /**
   * Draw calendar with current date
   */
  private populateCalendar = () => {
    const firstOfMonth: Moment = this.currentDate.startOf('month');
    const daysInMonth: number = this.currentDate.daysInMonth();

    const filler = [];
    const days = [];
    const disabled = [];

    this.labels.day = 0;

    const countFillerDays = firstOfMonth.weekday();
    const maxDays = this.maxDays ? parseInt(this.maxDays, 10) : 0;
    let countNormalDays = daysInMonth;
    let countDisabledDays = 0;

    if (this.currentDate.year() === moment().year()) {
      if (this.currentDate.month() === moment().month()) {
        this.labels.day = moment().date();
        if (maxDays > 0) {
          countNormalDays = moment().date() + maxDays
              > daysInMonth ? daysInMonth : moment().date() + maxDays;
          countDisabledDays = daysInMonth - countNormalDays;
        }
      } else if (this.currentDate.month() >= moment().month()) {
        if (maxDays > 0) {
          const remainingEnabledDays = this.currentDate.set('date', 1).diff(moment().add(maxDays, 'days'), 'days');
          if (remainingEnabledDays < 0) {
            countNormalDays = Math.abs(remainingEnabledDays);
            countDisabledDays = daysInMonth - countNormalDays;
          } else {
            countNormalDays = 0;
            countDisabledDays = daysInMonth;
          }
        }
      }
    }

    for (let i = 0; i < countFillerDays; i += 1) {
      filler.push('');
    }
    for (let i = 0; i < countNormalDays; i += 1) {
      days.push(i + 1);
    }
    for (let i = 0 + countNormalDays + 1; i < countDisabledDays + countNormalDays + 1; i += 1) {
      disabled.push(i);
    }

    this.fillerDays = filler;
    this.daysOfMonth = days;
    this.disabledDays = disabled;
    this.updateTexts();
  };
}
